'use client';

import { useState, useEffect } from 'react';
import { CarouselItem } from '@/components/ui/carousel/carousel';
import BasicCard from '@/components/common/basic-card';
import CarouselWithPagination from '@/components/common/carousel-with-pagination';

interface ExtraCredit {
  uid: string;
  url: string;
  title: string;
  date_created?: string;
  created_at?: string;
  category?: Array<{ title: string }>;
  resource_detail_sections?: Record<string, any>;
}

interface ExtraCreditsCarouselProps {
  extraCredits: ExtraCredit[];
}

export default function ExtraCreditsCarousel({ extraCredits }: ExtraCreditsCarouselProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const processedCredits = extraCredits.slice(0, 3).map((extraCredit) => {
    let thumbnail = { filename: '', url: '' };

    Object.entries(extraCredit?.resource_detail_sections ?? {}).find(
      ([, value]: [string, any]) => {
        if (value && typeof value === 'object' && 'preview_tile_section' in value) {
          const section: any = value.preview_tile_section;
          if (section && 'thumb' in section) {
            thumbnail = section.thumb;
            return true;
          }
        }
        return false;
      }
    );

    return {
      uid: extraCredit.uid,
      url: `https://contentstack.com${extraCredit.url}`,
      thumbnail,
      title: extraCredit.title,
      duration: new Date(
        extraCredit.date_created ?? extraCredit.created_at ?? Date.now()
      ).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      contentType: extraCredit.category?.[0]?.title || '',
    };
  });

  if (isMobile) {
    return (
      <CarouselWithPagination>
        {processedCredits.map((credit) => (
          <CarouselItem key={credit.uid} className="flex h-full pl-4 basis-[85%] sm:basis-1/2">
            <BasicCard
              url={credit.url}
              thumbnail={credit.thumbnail}
              title={credit.title}
              duration={credit.duration}
              contentType={credit.contentType}
              hoverVariant="homePage"
            />
          </CarouselItem>
        ))}
      </CarouselWithPagination>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
      {processedCredits.map((credit) => (
        <BasicCard
          key={credit.uid}
          url={credit.url}
          thumbnail={credit.thumbnail}
          title={credit.title}
          duration={credit.duration}
          contentType={credit.contentType}
          hoverVariant="homePage"
        />
      ))}
    </div>
  );
}
